import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Container, Stack, Typography } from "@mui/material";
import ArrowOutwardRoundedIcon from "@mui/icons-material/ArrowOutwardRounded";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import SkylineImg from "../../Assets/saudi-city-skyline-without-bg.png";

const BLOGS_API =
  "https://us-central1-aroundu-app.cloudfunctions.net/v1public/blogs/search/1/4";
const BLOG_SITE = "https://blog.aroundu.app";

const getImage = (blog) => {
  if (blog?.media_url?.url) return blog.media_url.url;
  if (typeof blog?.coverUrl === "string") return blog.coverUrl;
  return null;
};

const slugify = (str) =>
  (str || "")
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const getBlogUrl = (blog) =>
  `${BLOG_SITE}/blog/${slugify(blog.title)}?id=${encodeURIComponent(blog.id)}`;

const formatDate = (blog) => {
  const secs = blog?.publishedAt?._seconds || blog?.createdAt?._seconds;
  if (!secs) return "";
  return new Date(secs * 1000).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const BlogCard = ({ blog }) => {
  const { t } = useTranslation();
  const image = getImage(blog);
  const tag = Array.isArray(blog.tags) && blog.tags.length ? blog.tags[0] : null;
  const date = formatDate(blog);

  return (
    <Box
      component="a"
      href={getBlogUrl(blog)}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        display: "flex",
        flexDirection: "column",
        textDecoration: "none",
        borderRadius: 3,
        overflow: "hidden",
        backgroundColor: "#FFFFFF",
        border: "1px solid rgba(9, 34, 31, 0.08)",
        boxShadow: "0 20px 50px rgba(7, 20, 26, 0.06)",
        transition: "transform 0.25s ease, box-shadow 0.25s ease",
        "&:hover": {
          transform: "translateY(-6px)",
          boxShadow: "0 26px 60px rgba(7, 20, 26, 0.12)",
        },
      }}
    >
      <Box
        sx={{
          aspectRatio: "16 / 10",
          backgroundColor: "#e7f1ed",
          backgroundImage: image
            ? `url(${image})`
            : "linear-gradient(135deg, rgba(0,204,187,0.25), rgba(146,114,248,0.18))",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <Box sx={{ p: 1.75, display: "flex", flexDirection: "column", flex: 1 }}>
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
          {tag && (
            <Typography
              sx={{
                color: "#09998F",
                backgroundColor: "rgba(0, 204, 187, 0.12)",
                px: 0.9,
                py: 0.35,
                borderRadius: 999,
                fontSize: "0.65rem",
                fontWeight: 800,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
              }}
            >
              {tag}
            </Typography>
          )}
          {date && (
            <Typography sx={{ color: "rgba(16, 33, 30, 0.5)", fontSize: "0.72rem", fontWeight: 600 }}>
              {date}
            </Typography>
          )}
        </Stack>

        <Typography
          sx={{
            color: "#10211E",
            fontSize: "0.98rem",
            fontWeight: 800,
            lineHeight: 1.28,
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: 2,
            overflow: "hidden",
          }}
        >
          {blog.title}
        </Typography>

        {blog.description && (
          <Typography
            sx={{
              mt: 0.75,
              color: "rgba(16, 33, 30, 0.62)",
              fontSize: "0.82rem",
              lineHeight: 1.5,
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2,
              overflow: "hidden",
            }}
          >
            {blog.description}
          </Typography>
        )}

        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          sx={{ mt: "auto", pt: 1.25, color: "#00ccbb", fontWeight: 800, fontSize: "0.8rem" }}
        >
          <span>{t("read_article")}</span>
          <ArrowOutwardRoundedIcon sx={{ fontSize: 15 }} />
        </Stack>
      </Box>
    </Box>
  );
};

const SkeletonCard = () => (
  <Box
    sx={{
      borderRadius: 3,
      overflow: "hidden",
      backgroundColor: "#FFFFFF",
      border: "1px solid rgba(9, 34, 31, 0.08)",
      "@keyframes blogPulse": { "0%, 100%": { opacity: 1 }, "50%": { opacity: 0.5 } },
    }}
  >
    <Box sx={{ aspectRatio: "16 / 10", backgroundColor: "#e7f1ed", animation: "blogPulse 1.4s ease-in-out infinite" }} />
    <Box sx={{ p: 2.5 }}>
      <Box sx={{ width: "40%", height: 14, borderRadius: 1, backgroundColor: "#e7f1ed", mb: 1.5, animation: "blogPulse 1.4s ease-in-out infinite" }} />
      <Box sx={{ width: "90%", height: 18, borderRadius: 1, backgroundColor: "#e7f1ed", mb: 1, animation: "blogPulse 1.4s ease-in-out infinite" }} />
      <Box sx={{ width: "70%", height: 18, borderRadius: 1, backgroundColor: "#e7f1ed", animation: "blogPulse 1.4s ease-in-out infinite" }} />
    </Box>
  </Box>
);

const BlogSection = () => {
  const { t } = useTranslation();
  const [blogs, setBlogs] = useState([]);
  const [status, setStatus] = useState("loading"); // loading | done | error

  const sectionRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });
  const skylineX = useTransform(
    scrollYProgress,
    [0, 1],
    prefersReducedMotion ? ["0%", "0%"] : ["-3%", "3%"]
  );

  useEffect(() => {
    let active = true;
    fetch(BLOGS_API)
      .then((res) => res.json())
      .then((json) => {
        if (!active) return;
        setBlogs(Array.isArray(json?.data) ? json.data : []);
        setStatus("done");
      })
      .catch(() => {
        if (active) setStatus("error");
      });
    return () => {
      active = false;
    };
  }, []);

  // Hide the whole section if the blogs can't be loaded.
  if (status === "error" || (status === "done" && blogs.length === 0)) {
    return null;
  }

  // Grid item on desktop; fixed-width snap item on the mobile carousel.
  const itemSx = {
    flex: { xs: "0 0 80%", sm: "0 0 44%", md: "1 1 0" },
    scrollSnapAlign: { xs: "start", md: "none" },
    minWidth: 0,
  };


  return (
    <Box
      component="section"
      ref={sectionRef}
      sx={{
        position: "relative",
        overflow: "hidden",
        py: { xs: 6, md: 8 },
        backgroundColor: "#0A4A3F",
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          position: "absolute",
          left: "-6%",
          right: "-6%",
          bottom: 0,
          height: { xs: 260, md: 600 },
          zIndex: 0,
          pointerEvents: "none",
          overflow: "hidden",
        }}
      >
        <Box
          component={motion.img}
          src={SkylineImg}
          alt=""
          style={{ x: skylineX }}
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center bottom",
            filter: "invert(1)",
            opacity: 0.5,
          }}
        />
      </Box>

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Stack
          component={motion.div}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.35 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          spacing={1.5}
          alignItems="center"
          sx={{ textAlign: "center", mx: "auto", maxWidth: 760, mb: { xs: 4, md: 5 } }}
        >
          <Typography
            sx={{
              color: "#00D5C8",
              fontSize: { xs: "0.76rem", md: "0.82rem" },
              fontWeight: 850,
              letterSpacing: "0.24em",
              textTransform: "uppercase",
            }}
          >
            {t("blog_eyebrow")}
          </Typography>
          <Typography
            variant="h2"
            sx={{
              color: "#FFFFFF",
              fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
              fontWeight: 900,
              lineHeight: { xs: 1.06, md: 1.02 },
            }}
          >
            {t("blog_title")}
          </Typography>
          <Typography
            sx={{
              color: "rgba(224, 244, 242, 0.72)",
              fontSize: { xs: "0.95rem", md: "1.05rem" },
              lineHeight: 1.6,
              maxWidth: 560,
            }}
          >
            {t("blog_subtitle")}
          </Typography>
        </Stack>

        <Box
          dir="ltr"
          sx={{
            display: { xs: "flex", md: "grid" },
            gridTemplateColumns: { md: "repeat(4, 1fr)" },
            gap: { xs: 2, md: 2.5 },
            // mobile carousel
            overflowX: { xs: "auto", md: "visible" },
            scrollSnapType: { xs: "x mandatory", md: "none" },
            scrollPaddingLeft: { xs: "4px" },
            pt: { xs: 0.5, md: 0 },
            pb: { xs: 1.5, md: 0 },
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          {status === "loading"
            ? [0, 1, 2, 3].map((i) => (
                <Box key={i} sx={itemSx}>
                  <SkeletonCard />
                </Box>
              ))
            : blogs.map((blog) => (
                <Box key={blog.id} sx={itemSx}>
                  <BlogCard blog={blog} />
                </Box>
              ))}
        </Box>


        <Stack alignItems="center" sx={{ mt: { xs: 4, md: 5 } }}>
          <Button
            component="a"
            href={BLOG_SITE}
            target="_blank"
            rel="noopener noreferrer"
            endIcon={<ArrowOutwardRoundedIcon />}
            sx={{
              px: { xs: 2.5, md: 3 },
              py: { xs: 1.05, md: 1.18 },
              minHeight: 46,
              borderRadius: 999,
              color: "#FFFFFF",
              backgroundColor: "#00ccbb",
              fontSize: { xs: "0.9rem", md: "0.95rem" },
              fontWeight: 800,
              letterSpacing: 0,
              textTransform: "none",
              boxShadow: "0 16px 36px rgba(0, 204, 187, 0.28)",
              "&:hover": {
                backgroundColor: "#00b8a8",
                boxShadow: "0 18px 40px rgba(0, 204, 187, 0.36)",
              },
            }}
          >
            {t("see_more_blogs")}
          </Button>
        </Stack>
      </Container>
    </Box>
  );
};

export default BlogSection;
